import { useEffect } from 'react';
import {
  generateSportsEventSchema,
  generateWebPageSchema,
  generateArticleSchema,
  generateBreadcrumbListSchema,
  generateFAQPageSchema,
  type MatchData,
  type FAQItem,
  type BreadcrumbItem,
} from '@/lib/schemaUtils';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  jsonLd?: object;
  noindex?: boolean;
}

/**
 * SEO Head Component - Updates document metadata for SEO
 * 
 * Handles:
 * - Document title
 * - Meta description
 * - Open Graph tags
 * - Twitter Card tags
 * - Canonical URL
 * - JSON-LD structured data
 */
export function SEOHead({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage = 'https://winwgo.com/og-image.png',
  jsonLd,
  noindex = false,
}: SEOHeadProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper to update or create meta tags
    const updateMeta = (selector: string, content: string, isProperty = false) => {
      let meta = document.querySelector(selector) as HTMLMetaElement | null;
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', selector.replace('meta[property="', '').replace('"]', ''));
        } else {
          meta.setAttribute('name', selector.replace('meta[name="', '').replace('"]', ''));
        }
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    // Update meta description
    updateMeta('meta[name="description"]', description);

    // Update robots meta if noindex
    if (noindex) {
      updateMeta('meta[name="robots"]', 'noindex, nofollow');
    } else {
      updateMeta('meta[name="robots"]', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    // Update Open Graph tags
    updateMeta('meta[property="og:title"]', title, true);
    updateMeta('meta[property="og:description"]', description, true);
    updateMeta('meta[property="og:type"]', ogType, true);
    updateMeta('meta[property="og:site_name"]', 'WinWGo', true);
    updateMeta('meta[property="og:image"]', ogImage, true);
    updateMeta('meta[property="og:image:width"]', '1200', true);
    updateMeta('meta[property="og:image:height"]', '630', true);
    updateMeta('meta[property="og:locale"]', 'en_US', true);
    
    if (canonical) {
      updateMeta('meta[property="og:url"]', canonical, true);
    }

    // Update Twitter Card tags
    updateMeta('meta[name="twitter:card"]', 'summary_large_image');
    updateMeta('meta[name="twitter:title"]', title);
    updateMeta('meta[name="twitter:description"]', description);
    updateMeta('meta[name="twitter:image"]', ogImage);
    updateMeta('meta[name="twitter:site"]', '@WinWGo');

    // Update canonical link
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (link) {
        link.setAttribute('href', canonical);
      } else {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', canonical);
        document.head.appendChild(link);
      }
    }

    // Add JSON-LD structured data
    if (jsonLd) {
      // Remove any existing JSON-LD scripts we added
      const existingScripts = document.querySelectorAll('script[data-seo-jsonld]');
      existingScripts.forEach(script => script.remove());

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      // Reset to defaults on unmount
      document.title = 'WinWGo - Expert Football Predictions';
      
      // Remove JSON-LD
      const jsonLdScripts = document.querySelectorAll('script[data-seo-jsonld]');
      jsonLdScripts.forEach(script => script.remove());
    };
  }, [title, description, canonical, ogType, ogImage, jsonLd, noindex]);

  return null;
}

// Re-export schema utilities for convenience
export { generateSportsEventSchema, generateWebPageSchema, generateArticleSchema, generateBreadcrumbListSchema, generateFAQPageSchema };

/**
 * Generate JSON-LD for the home page (WebSite + Organization + FAQPage schemas)
 */
export function generateWebsiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': 'https://winwgo.com/#website',
        name: 'WinWGo',
        url: 'https://winwgo.com',
        description: 'Expert football predictions and analysis for top leagues worldwide.',
        inLanguage: 'en-US',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: 'https://winwgo.com/match/{search_term}',
          },
          'query-input': 'required name=search_term',
        },
        publisher: {
          '@id': 'https://winwgo.com/#organization',
        },
      },
      {
        '@type': 'Organization',
        '@id': 'https://winwgo.com/#organization',
        name: 'WinWGo',
        url: 'https://winwgo.com',
        logo: {
          '@type': 'ImageObject',
          url: 'https://winwgo.com/favicon.png',
          width: 512,
          height: 512,
        },
        description: 'Expert football predictions and betting tips powered by AI analysis.',
        sameAs: [
          'https://twitter.com/WinWGo',
        ],
      },
      {
        '@type': 'WebPage',
        '@id': 'https://winwgo.com/#webpage',
        url: 'https://winwgo.com',
        name: 'WinWGo - Expert Football Predictions & Betting Tips',
        description: 'Get expert football predictions and analysis for Premier League, La Liga, Serie A, and top leagues worldwide.',
        isPartOf: {
          '@id': 'https://winwgo.com/#website',
        },
        inLanguage: 'en-US',
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: 'https://winwgo.com/og-image.png',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How accurate are WinWGo football predictions?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Our AI-powered predictions analyze team form, head-to-head records, player statistics, and market odds to deliver expert-level insights with high accuracy rates across major leagues.',
            },
          },
          {
            '@type': 'Question',
            name: 'Which football leagues does WinWGo cover?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'WinWGo provides predictions for all major leagues including Premier League, La Liga, Serie A, Bundesliga, Ligue 1, Champions League, and Europa League.',
            },
          },
          {
            '@type': 'Question',
            name: 'How do I access match predictions?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Simply browse our homepage for today\'s matches, select a game, and unlock the expert analysis using credits. Each prediction includes detailed betting tips and key factors.',
            },
          },
        ],
      },
    ],
  };
}

/**
 * Generate JSON-LD for a sports event (match)
 * Uses the unified schema utility for consistent, valid structured data
 */
export function generateMatchJsonLd(match: {
  id?: string;
  home_team: string;
  away_team: string;
  kickoff_time: string;
  venue?: string | null;
  league?: { name: string; logo_url?: string | null; country?: string | null } | null;
  status: string;
  home_score?: number | null;
  away_score?: number | null;
  home_team_logo?: string | null;
  away_team_logo?: string | null;
}, slug: string) {
  const matchUrl = `https://winwgo.com/match/${slug}`;
  
  // Use the unified schema utility for valid SportsEvent with location
  return generateSportsEventSchema(match as MatchData, matchUrl);
}

/**
 * Generate BreadcrumbList JSON-LD
 * Uses the unified schema utility for consistency
 */
export function generateBreadcrumbJsonLd(items: { name: string; url: string }[]) {
  return generateBreadcrumbListSchema(items as BreadcrumbItem[]);
}

/**
 * Generate Article JSON-LD for prediction content
 * Uses the unified schema utility for consistency
 */
export function generateArticleJsonLd(match: {
  home_team: string;
  away_team: string;
  kickoff_time: string;
  league?: { name: string } | null;
}, slug: string, predictionDate?: string) {
  const matchUrl = `https://winwgo.com/match/${slug}`;
  
  return generateArticleSchema({
    url: matchUrl,
    headline: `${match.home_team} vs ${match.away_team} Prediction & Betting Tips`,
    description: `Expert analysis and betting tips for ${match.home_team} vs ${match.away_team} in ${match.league?.name || 'Football'}. AI-powered predictions with key factors and insights.`,
    datePublished: predictionDate,
    keywords: [
      match.home_team,
      match.away_team,
      match.league?.name || 'Football',
      'predictions',
      'betting tips',
      'football analysis',
    ],
  });
}

/**
 * Generate FAQPage JSON-LD for dedicated FAQ pages
 * Uses the unified schema utility for consistency
 */
export function generateFAQPageJsonLd(faqs: { question: string; answer: string }[]) {
  return generateFAQPageSchema(faqs as FAQItem[]);
}
